import React, { Fragment, useEffect, useState } from 'react';
import {
  useDispatch, shallowEqual, useSelector, batch
} from 'react-redux';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import ReactTooltip from 'react-tooltip';

import Layout from '../../../layout/Layout';
import { permissions } from '../../../common/permissionGroup';
import AccessDenied from '../../../components/AccessDenied/AccessDenied';
import Card from '../../../components/Card/Card';
import PermissionsGate from '../../../components/PermissionsGate/PermissionsGate';
import ButtonTypes from '../../../constants/buttonTypes';
import SearchInput from '../../../components/SearchInput/SearchInput';
import Pagination from '../../../components/Pagination/Pagination';
import Table from '../../../components/Table/Table';
import TableHead from '../../../components/Table/TableHead';
import TableRow from '../../../components/Table/TableRow';
import TableHeadData from '../../../components/Table/TableHeadData';
import TableBody from '../../../components/Table/TableBody';
import TableData from '../../../components/Table/TableData';
import Badge from '../../../components/Badge/Badge';
import Text from '../../../components/Text/Text';
import EmptyTable from '../../../components/Table/EmptyTable';
import { coOrdersTable } from '../../../constants/coOrdersTable';
import { setCoOrdersFilter } from '../../../actions/coilovers/orders/coOrdersFilter';
import getCoOrders from '../../../thunks/coilovers/orders/getCoOrders';
import CoOrdersActions from './components/CoOrdersActions';
import {
  IconArrowDown, IconCard, IconCross, IconFileOk, IconOk, IconWarningTriangle
} from '../../../assets/icon';
import { urgencyStatusInfo } from '../../../constants/urgencyStatus';
import coOrderStatus, { coOrderStatusInfo } from '../../../constants/coOrderStatus';
import CoOrderRightHeader from './components/CoOrderRightHeader';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import { clearPatchCoOrders } from '../../../actions/coilovers/orders/patchCoOrders';
import CoOrderEditModal from './components/CoOrderEditModal';
import CoOrderLateCorrectionModal from './components/CoOrderLateCorrectionModal';
import actionCoOrder, { clearActionCoOrder } from '../../../thunks/coilovers/orders/actionCoOrder';
import prepareProviderLink from '../../../helpers/general';
import orderActionTypes from '../../../constants/orderActionTypes';
import Button from '../../../components/Button/Button';
import { setApplicationState } from '../../../actions/application/application';
import CoOrderSubheader from './components/CoOrderSubheader';
import useQueryParams from '../../../hooks/useQueryParams';
import CoOrderAvailabilityModal from './components/CoOrderAvailabilityModal';
import zebraPrintFile, { clearZebraPrintFile } from '../../../thunks/zebraPrinter/printZebraFile';
import printWrLabel from '../../../thunks/zebraPrinter/printWrLabel';
import CoMissingPartsModal from './components/CoMissingPartsModal';
import printProductionLabel from '../../../thunks/zebraPrinter/printProductionLabel';
import useBarcodeScanner from '../../../hooks/useBarcodeScanner';
import CoOrderCreateLabelModal from './components/CoOrderCreateLabelModal';
import postPusherOrderRowSelect from '../../../thunks/general/pusher/postPusherOrderRowSelect';
import CoOrderViewInvoiceModal from './components/CoOrderViewInvoiceModal';
import patchCoOrder from '../../../thunks/coilovers/orders/patchCoOrder';
import coOrderAssemblyType from '../../../constants/coOrderAssemblyType';
import PersonalReportDrawer from './components/PersonalReportDrawer';
import CoOrderDetailsRow from './components/CoOrderDetailsRow';

const searchHeaderClassName = 'w-[250px]';
const tableContainerClassName = 'overflow-auto';
const tableCellClassName = 'min-w-max whitespace-nowrap px-[9px] py-0 relative';
const actionCellClassName = 'relative w-[60px] py-0 pl-0 pr-[9px]';
const userBadgeClassName = 'absolute text-[11px] top-0 z-[10]';
const trackingIconClassName = 'absolute top-[7px] left-0';
const additionalButtonClassName = 'min-w-[80px] px-[5px] py-[5px] whitespace-nowrap';
const detailsButtonClassName = `inline-flex h-[30px] w-[30px] items-center justify-center rounded text-primary
  hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/30`;
const labelContainerClassName = 'ml-[6px]';
const labelTextClassName = 'text-[12px]';
const mobileVisibleOrderFields = [
  'Status',
  'Mpn',
  'Lift position',
  'Reference no',
];

const Orders = () => {
  const dispatch = useDispatch();
  const {
    enqueueSnackbar,
    closeSnackbar
  } = useSnackbar();
  const [timer, setTimer] = useState();
  const [lastCode, setLastCode] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [editModalVisibility, setEditModalVisibility] = useState(false);
  const [availabilityModalVisibility, setAvailabilityModalVisibility] = useState(false);
  const [labelModalVisibility, setLabelModalVisibility] = useState(false);
  const [modalMissingParts, setModalMissingParts] = useState(false);
  const [modalViewInvoice, setModalViewInvoice] = useState(false);
  const [editItem, setEditItem] = useState();
  const [lateCorrectionItem, setLateCorrectionItem] = useState(null);
  const [activeId, setActiveId] = useState();
  const [actionType, setActionType] = useState();
  const [activeLabelFile, setActiveLabelFile] = useState(false);
  const [activeLabelFile2, setActiveLabelFile2] = useState(false);
  const [confirmPrintLabelModal, setConfirmPrintLabelModal] = useState(false);
  const [expandedOrderIds, setExpandedOrderIds] = useState(() => new Set());
  const [queryParams, setQueryParams] = useQueryParams();

  const {
    coOrdersFilter,
    coOrdersFilter: {
      query,
      size,
      order
    },
    getCoOrders: {
      loading,
      data,
      currentPage,
      lastPage
    },
    actionCoOrder: {
      loading: actionLoading,
      error: actionError,
      data: actionResult,
    },
    application: {
      selectedRow = [],
      coOrderTableSetting,
      otherUserSelectedRow = [],
    },
    postPrintZebraFile,
    me,
    usersOnline,
  } = useSelector((state) => ({
    coOrdersFilter: state?.coOrdersFilter,
    getCoOrders: state?.getCoOrders,
    actionCoOrder: state?.actionCoOrder,
    application: state?.application,
    postPrintZebraFile: state?.postPrintZebraFile,
    me: state?.getMyProfile?.user,
    usersOnline: state?.userOnlineList?.users || [],
  }), shallowEqual);

  const selectedRows = Array.isArray(selectedRow) ? selectedRow : [];
  const otherUserSelectedRows = Array.isArray(otherUserSelectedRow) ? otherUserSelectedRow : [];

  const actionData = {
    [orderActionTypes.RELEASE]: {
      buttonText: 'Release',
      confirmText: 'Reserved qty will be available for other orders.',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: 'Order released successfully',
    },
    [orderActionTypes.CANCEL]: {
      buttonText: 'Cancel',
      confirmText: 'The order will be released first.',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: 'Order canceled successfully',
    },
    [[orderActionTypes.ARCHIVE]]: {
      buttonText: 'Archive',
      confirmText: 'The order will be released first.',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: 'Order archived successfully',
    },
    [orderActionTypes.LOST_IN_PROCESS]: {
      buttonText: 'Lost in process',
      confirmText: 'Archive this order with a LOST-IN-PROCESS note? '
        + 'Tracking and labels will be preserved. Stock will not change.',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: 'Order archived as lost in process',
    },
    [orderActionTypes.DONE_ORDER]: {
      buttonText: 'Written off',
      confirmText: 'Order item will be written off',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: 'Order done successfully',
    },
    [orderActionTypes.CHECK_AVAILABILITY]: {
      buttonText: 'Check',
      confirmText: 'Available item will be reserved',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: actionResult?.message,
      responseData: actionResult?.errorData,
    },
    [orderActionTypes.PART_BOX]: {
      buttonText: 'Part Box',
      confirmText: 'Order item will be written off',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: actionResult?.message,
    },
    [orderActionTypes.PICKED]: {
      buttonText: 'Pick parts',
      confirmText: 'Order status will be set to Picked',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: actionResult?.message,
    },
    [orderActionTypes.PACK_BOX]: {
      buttonText: 'Assembled',
      confirmText: 'Order status will be set to Pack box',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: actionResult?.message,
    },
    [orderActionTypes.ORDER_LABEL]: {
      buttonText: 'Done order',
      confirmText: 'Order status will be set to Packed',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: actionResult?.message,
    },
    [orderActionTypes.ORDER_TO_OVERSTOCK]: {
      buttonText: 'To overstock',
      confirmText: 'Order will be moved to overstock if it is not already in transit.',
      onConfirm: () => dispatch(actionCoOrder({
        id: activeId,
        type: actionType
      })),
      successMessage: actionResult?.message,
      type: actionResult?.type || 'success',
    }
  };

  const additionalOrderData = {
    [coOrderStatus.READY_ASB]: {
      buttonText: 'Pick parts',
      type: ButtonTypes.LIGHT_INFO,
      onClick: (id) => {
        setActiveId(id);
        setActionType(orderActionTypes.PICKED);
        setConfirmModal(true);
      }
    },
    [coOrderStatus.PICKED]: {
      buttonText: 'Part Box',
      type: ButtonTypes.LIGHT_PRIMARY,
      onClick: (id) => {
        setActiveId(id);
        setActionType(orderActionTypes.PART_BOX);
        setConfirmModal(true);
      }
    },
    [coOrderStatus.PART_BOX]: {
      buttonText: 'Assembled',
      type: ButtonTypes.LIGHT_WARNING,
      onClick: (id, labelFile, labelFile2) => {
        setActiveId(id);
        setActionType(orderActionTypes.PACK_BOX);
        setConfirmModal(true);
        if (labelFile?.includes('.png')
          || labelFile?.includes('.jpg')
          || labelFile?.includes('.pdf')
          || labelFile?.includes('.jpeg')
        ) {
          setActiveLabelFile(labelFile);
        }
        if (labelFile2?.includes('.png')
          || labelFile2?.includes('.jpg')
          || labelFile2?.includes('.pdf')
          || labelFile2?.includes('.jpeg')
        ) {
          setActiveLabelFile2(labelFile2);
        }
      }
    },
    [coOrderStatus.PACK_BOX]: {
      buttonText: 'Packed',
      type: ButtonTypes.LIGHT_SUCCESS,
      onClick: (id) => {
        setActiveId(id);
        setActionType(orderActionTypes.ORDER_LABEL);
        setConfirmModal(true);
      }
    },
  };

  const action = (id) => (
    <div role="presentation" onClick={() => closeSnackbar(id)}>
      <IconCross />
    </div>
  );

  const onPagePress = (page) => batch(() => {
    batch(() => {
      dispatch(setCoOrdersFilter({ page }));
      dispatch(getCoOrders());
    });
  });

  const onSizeChange = ({ value }) => {
    batch(() => {
      dispatch(setCoOrdersFilter({ size: value }));
      dispatch(getCoOrders());
    });
  };

  const onSort = (isSort, sortStr) => {
    if (isSort) {
      batch(() => {
        dispatch(setCoOrdersFilter({ order: sortStr }));
        dispatch(getCoOrders());
      });
    }
  };
  const selectRow = (id) => {
    const rowsSelected = selectedRows.includes(id) ? selectedRows.filter((row) => row !== id) : [id];
    const pusherData = {
      user: me,
      rowSelected: rowsSelected,
    };
    // const notMySelectedRows = otherUserSelectedRow.filter((row) => !rowsSelected?.includes(row));
    const otherUserSelectedRowTest = [
      ...otherUserSelectedRows.filter(({ user }) => user?.id !== me?.id),
      pusherData
    ];
    dispatch(setApplicationState({
      selectedRow: rowsSelected,
    }));

    dispatch(postPusherOrderRowSelect(otherUserSelectedRowTest));
  };

  const toggleOrderDetails = (id) => {
    setExpandedOrderIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(id)) {
        nextIds.delete(id);
      } else {
        nextIds.add(id);
      }
      return nextIds;
    });
  };

  const getColumn = () => {
    if (coOrderTableSetting?.length) {
      return coOrdersTable.filter(({ source }) => coOrderTableSetting.includes(source));
    }
    return coOrdersTable;
  };

  const onPrintLabel = (labelFile, id = 'fa', esId = '') => {
    if (labelFile.includes('.png')
      || labelFile.includes('.jpg')
      || labelFile.includes('.pdf')
      || labelFile.includes('.jpeg')
    ) {
      dispatch(zebraPrintFile({
        file: labelFile,
        order_id: id,
        esId,
      }));
    } else {
      enqueueSnackbar('Unavailable label format', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      });
    }
  };

  const onPrintWrLabel = (id) => {
    dispatch(printWrLabel({ id }));
    enqueueSnackbar('Prod Label send to printer', {
      variant: 'success',
      autoHideDuration: 5000
    });
  };

  const onPrintProductLabel = (id) => {
    dispatch(printProductionLabel({ id }));
    enqueueSnackbar('Product Label send to printer', {
      variant: 'success',
      autoHideDuration: 5000
    });
  };

  useEffect(() => {
    if (postPrintZebraFile.error) {
      enqueueSnackbar(postPrintZebraFile.error, {
        variant: 'error',
        persist: true,
        action: (id) => action(id)
      });
      dispatch(clearZebraPrintFile());
    }
    if (postPrintZebraFile?.data?.status) {
      enqueueSnackbar(`Label send to printer ${postPrintZebraFile?.data?.status}`, {
        variant: 'success',
        autoHideDuration: 5000
      });
      dispatch(clearZebraPrintFile());
    }
  }, [postPrintZebraFile]);

  useEffect(() => {
    clearTimeout(timer);
    const t = setTimeout(() => batch(() => {
      dispatch(setCoOrdersFilter({ page: 1 }));
      dispatch(getCoOrders());
    }), 800);
    setTimer(t);
  }, [query]);

  useEffect(() => {
    const filterData = {
      ...(queryParams.query?.length && { query: queryParams.query }),
      ...(queryParams.order?.length && { order: queryParams.order }),
      ...(queryParams?.orderStatus?.length && { orderStatus: queryParams?.orderStatus.split(',') }),
      ...(queryParams?.orderAssemblyType?.length && { orderAssemblyType: queryParams?.orderAssemblyType.split(',') }),
      ...(queryParams?.orderAssemblyStyle?.length
        && { orderAssemblyStyle: queryParams?.orderAssemblyStyle.split(',') }),
      ...(queryParams?.marketplaceDateFrom?.length && { marketplaceDateFrom: queryParams?.marketplaceDateFrom }),
      ...(queryParams?.marketplaceDateTo?.length && { marketplaceDateTo: queryParams?.marketplaceDateTo }),
      ...(queryParams?.assembledDateTo?.length && { assembledDateTo: queryParams?.assembledDateTo }),
      ...(queryParams?.assembledDateFrom?.length && { assembledDateFrom: queryParams?.assembledDateFrom }),
    };

    dispatch(setCoOrdersFilter(filterData));

    return batch(() => {
      dispatch(clearPatchCoOrders());
    });
  }, []);

  useEffect(() => {
    if (actionResult?.errorData) {
      setConfirmModal(false);
      setAvailabilityModalVisibility(true);
    } else if (actionResult) {
      enqueueSnackbar(actionData[actionType].successMessage || 'Action success', {
        variant: actionResult?.type || 'success',
        autoHideDuration: 5000
      });
      batch(() => {
        dispatch(clearActionCoOrder());
        if (activeLabelFile || activeLabelFile2) {
          setConfirmPrintLabelModal(false);
        }
        setConfirmModal(false);
      });
      setConfirmModal(false);
    }
    if (actionError) {
      enqueueSnackbar(actionError, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        persist: true,
        action: (id) => action(id)
      });
      dispatch(clearActionCoOrder());
    }
  }, [actionResult, actionError]);

  useEffect(() => {
    setQueryParams({
      ...(query && { query }),
      ...(order && { order }),
      ...(coOrdersFilter?.orderStatus?.length && { orderStatus: coOrdersFilter?.orderStatus.join(',') }),
      ...(coOrdersFilter?.orderAssemblyType?.length
        && { orderAssemblyType: coOrdersFilter?.orderAssemblyType.join(',') }),
      ...(coOrdersFilter?.orderAssemblyStyle?.length
        && { orderAssemblyStyle: coOrdersFilter?.orderAssemblyStyle.join(',') }),
      ...(coOrdersFilter?.marketplaceDateTo && { marketplaceDateTo: coOrdersFilter?.marketplaceDateTo }),
      ...(coOrdersFilter?.marketplaceDateFrom && { marketplaceDateFrom: coOrdersFilter?.marketplaceDateFrom }),
      ...(coOrdersFilter?.assembledDateTo && { assembledDateTo: coOrdersFilter?.assembledDateTo }),
      ...(coOrdersFilter?.assembledDateFrom && { assembledDateFrom: coOrdersFilter?.assembledDateFrom }),
    });
  }, [coOrdersFilter]);

  useBarcodeScanner((code) => {
    setLastCode(code);
  });

  const getOtherUserSelectedData = () => (otherUserSelectedRows.length ? otherUserSelectedRows : [])
      ?.filter(({ user }) => user?.id !== me?.id);

  const otherUserSelectedRowIds = getOtherUserSelectedData()?.map(({ rowSelected }) => rowSelected).flat();

  return (
    <Layout pageTitle="CO Orders" subheader={<CoOrderSubheader />}>
      <PermissionsGate
        can={permissions.COW_READ}
        errorComponent={<AccessDenied />}
      >
        <Card
          showHeader
          headerLeft={(
            <div className={searchHeaderClassName}>
              <SearchInput
                isClearable
                value={query}
                onChange={({ target: { value } }) => dispatch(setCoOrdersFilter({ query: value }))}
              />
            </div>
          )}
          headerRight={<CoOrderRightHeader />}
          headerBordered={false}
          footerBordered={false}
          footer={(
            <div className="flex justify-end">
              <Pagination
                currentPage={currentPage}
                onPagePress={onPagePress}
                lastPage={lastPage}
                withPaginationSize
                onSizeChange={onSizeChange}
                size={size}
              />
            </div>
          )}
        >
          {lastCode && (
            <Text text={`Last scanned barcode: ${lastCode}`} />
          )}
          <div className={tableContainerClassName}>
            <Table loading={loading} mobileVisibleFields={mobileVisibleOrderFields}>
              <TableHead>
                <TableRow bordered>
                  <TableHeadData />
                  <PermissionsGate can={permissions.COW_UPDATE}>
                    <TableHeadData />
                    <TableHeadData />
                  </PermissionsGate>
                  {
                    getColumn()
                      .map(({
                        title,
                        source,
                        sortable
                      }) => (
                        <TableHeadData
                          key={source}
                          sortable={sortable}
                          order={order}
                          source={source}
                          onSort={(val) => onSort(sortable, val)}
                        >
                          {title}
                        </TableHeadData>
                      ))
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.length ? data.map((orderData, index) => {
                  const {
                    _id,
                    order_id: id,
                    order_assembly_type: type,
                    order_assembly_style: asStyle,
                    order_assembly_urgency: urgency,
                    order_marketplace_sold_date: soldDate,
                    order_status: status,
                    order_item_mpn: mpn,
                    order_car_lift_position: lift,
                    order_notes: notes,
                    order_listing_car_make: carMake,
                    order_listing_car_model: carModel,
                    order_listing_car_year_range: carYearRange,
                    order_label_file: labelFile,
                    order_label_file2: labelFile2,
                    order_assembly_invoice: invoiceFile,
                    order_reference_no: reference,
                    order_shipment_tracking_number: tracking,
                    order_assembly_supplier: supplier,
                    order_assembled_date: assembledDate,
                    order_picked_date: pickedDate,
                    order_packed_by: orderPackedBy,
                    order_part_box_by: orderPartBoxBy,
                    order_picked_by: orderPickedBy,
                    order_assembled_by: orderAssembledBy,
                    order_eta_date: ETA,
                    order_assembly_price: price,
                    order_assembly_cost: cost,
                    order_ebay_send_result: orderEbaySendResult,
                    order_stock_type: orderStockType,
                  } = orderData;
                  const trackingArr = tracking?.split(',') || [];
                  const userSelected = getOtherUserSelectedData()
                    ?.filter(({ rowSelected: r }) => r?.includes(_id))[0];
                  const isUserOnline = usersOnline
                    ?.find((userOnline) => userOnline.id === `${userSelected?.user?.id}`);

                  const detailsExpanded = expandedOrderIds.has(_id);

                  return (
                    <Fragment key={`order_${_id}`}>
                      <TableRow
                        bordered={index < data.length - 1}
                        selected={selectedRows.includes(_id)}
                        otherSelected={isUserOnline && otherUserSelectedRowIds?.includes(_id)}
                        onClick={() => selectRow(_id)}
                        dataRow={_id}
                      >
                        <TableData
                          className="orderDetailsControl w-[42px] min-w-[42px] px-[5px] py-0 text-center"
                        >
                          <button
                            type="button"
                            className={detailsButtonClassName}
                            aria-expanded={detailsExpanded}
                            aria-controls={`order-details-${_id}`}
                            aria-label={`${detailsExpanded ? 'Hide' : 'Show'} details for order ${id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleOrderDetails(_id);
                            }}
                          >
                            <IconArrowDown
                              width={18}
                              height={18}
                              className={`transition-transform ${detailsExpanded ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </TableData>
                        <PermissionsGate can={permissions.COW_UPDATE}>
                          <TableData className={actionCellClassName}>
                            {isUserOnline && userSelected && (
                              <Badge
                                text={`by ${userSelected?.user?.name}`}
                                type={ButtonTypes.LIGHT_INFO}
                                containerStyle={userBadgeClassName}
                              />
                            )}
                            <CoOrdersActions
                              isLoading={actionLoading}
                              // actionLoading
                              status={status}
                              isOverstock={orderStockType === 'overstock'}
                              side={index < data.length - 3 ? 'right' : 'top'}
                              onEdit={() => {
                                setEditItem(data[index]);
                                setEditModalVisibility(true);
                              }}
                              onLateCorrection={() => setLateCorrectionItem(data[index])}
                              onRelease={() => {
                                setActiveId(_id);
                                setActionType(orderActionTypes.RELEASE);
                                setConfirmModal(true);
                              }}
                              onArchived={() => {
                                setActiveId(_id);
                                setActionType(orderActionTypes.ARCHIVE);
                                setConfirmModal(true);
                              }}
                              onLostInProcess={() => {
                                setActiveId(_id);
                                setActionType(orderActionTypes.LOST_IN_PROCESS);
                                setConfirmModal(true);
                              }}
                              onCancel={() => {
                                setActiveId(_id);
                                setActionType(orderActionTypes.CANCEL);
                                setConfirmModal(true);
                              }}
                              onDoneOrder={() => {
                                setActiveId(_id);
                                setActionType(orderActionTypes.DONE_ORDER);
                                setConfirmModal(true);
                              }}
                              onCheckAvailability={() => {
                                setActiveId(_id);
                                setActionType(orderActionTypes.CHECK_AVAILABILITY);
                                setConfirmModal(true);
                              }}
                              onMissingParts={() => {
                                setActiveId(_id);
                                setModalMissingParts(true);
                              }}
                              onCreateShipLabel={
                                () => {
                                  setActiveId(_id);
                                  setLabelModalVisibility(true);
                                }
                              }
                              onPrintLabel={() => {
                                onPrintLabel(labelFile, id, _id);
                                onPrintLabel(labelFile2, id, _id);
                              }}
                              onPrintWrLabel={() => {
                                onPrintWrLabel(_id);
                              }}
                              onPrintProductLabel={() => {
                                onPrintProductLabel(_id);
                              }}
                              onInvoiceView={() => {
                                setActiveId(_id);
                                setModalViewInvoice(true);
                              }}
                              onOrderIssue={() => {
                                dispatch(patchCoOrder({
                                  _id,
                                  formData: {
                                    order_assembly_type: coOrderAssemblyType.ORDER_ISSUE
                                  }
                                }));
                              }}
                              toOverstock={type === 'Assembly' ? () => {
                                setActiveId(_id);
                                setActionType(orderActionTypes.ORDER_TO_OVERSTOCK);
                                setConfirmModal(true);
                              } : undefined}
                            />
                          </TableData>
                          <TableData>
                            {additionalOrderData[status] && (
                              <Button
                                type={additionalOrderData[status].type}
                                text={additionalOrderData[status].buttonText}
                                onClick={() => additionalOrderData[status].onClick(_id, labelFile, labelFile2)}
                                containerStyle={additionalButtonClassName}
                              />
                              )}
                          </TableData>
                        </PermissionsGate>
                        {
                          coOrderTableSetting.includes('order_id')
                          && <TableData className={tableCellClassName}>{id || ''}</TableData>
                        }
                        {
                          coOrderTableSetting.includes('order_assembly_type')
                          && (
                            <TableData className={tableCellClassName}>
                              <Badge text={type} type={ButtonTypes[invoiceFile ? 'DANGER' : 'LIGHT']} />
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_assembly_style')
                          && (
                            <TableData className={tableCellClassName}>
                              {asStyle && <Badge text={asStyle} type={ButtonTypes.LIGHT} />}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_assembly_urgency')
                          && (
                            <TableData className={tableCellClassName}>
                              {urgency && (
                                <Badge
                                  text={urgency}
                                  type={urgencyStatusInfo?.[urgency]?.state}
                                />
                              )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_marketplace_sold_date')
                          && (
                            <TableData className={tableCellClassName}>
                              {soldDate
                                && (
                                  <Badge
                                    type={ButtonTypes.LIGHT}
                                    text={DateTime.fromFormat(soldDate, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' })
                                      .setLocale()
                                      .toRelative()}
                                  />
                                )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_status')
                          && (
                            <TableData className={tableCellClassName}>
                              <div className="d-flex d-row">
                                {status && (
                                  <Badge
                                    type={coOrderStatusInfo?.[status]?.state}
                                    text={status}
                                  />
                                )}
                                {
                                  orderStockType === 'overstock' && (
                                    <Text
                                      text=""
                                      textStyle={labelTextClassName}
                                      styleContainer={labelContainerClassName}
                                      tooltip="Stock Type: Overstock"
                                    />
                                  )
                                }
                              </div>
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_item_mpn')
                          && (
                            <TableData className={tableCellClassName}>
                              <Text text={mpn || ''} bold />
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_car_lift_position')
                          && (
                            <TableData className={tableCellClassName}>
                              {lift}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_notes')
                          && (
                            <TableData className={tableCellClassName}>
                              {notes}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_listing_car_make')
                          && (
                            <TableData className={tableCellClassName}>
                              {carMake}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_listing_car_model')
                          && (
                            <TableData className={tableCellClassName}>
                              {carModel}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_listing_car_year_range')
                          && (
                            <TableData className={tableCellClassName}>
                              {carYearRange}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_label_file')
                          && (
                            <TableData className={tableCellClassName}>
                              {(labelFile?.includes('.png')
                                  || labelFile?.includes('.jpg')
                                  || labelFile?.includes('.jpeg')
                                  || labelFile?.includes('.pdf'))
                                && (
                                  <a href={labelFile} target="_blank" rel="noreferrer">
                                    <IconFileOk color="#7239EA" />
                                  </a>
                                )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_label_file2')
                          && (
                            <TableData className={tableCellClassName}>
                              {(labelFile2?.includes('.png')
                                  || labelFile2?.includes('.jpg')
                                  || labelFile2?.includes('.jpeg')
                                  || labelFile2?.includes('.pdf'))
                                && (
                                  <a href={labelFile2} target="_blank" rel="noreferrer">
                                    <IconFileOk color="#7239EA" />
                                  </a>
                                )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_assembly_invoice')
                          && (
                            <TableData className={tableCellClassName}>
                              {invoiceFile && (
                                <a href={invoiceFile} target="_blank" rel="noreferrer">
                                  <IconCard color="#7239EA" />
                                </a>
                              )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_reference_no')
                          && (
                            <TableData className={tableCellClassName}>
                              {reference}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_shipment_tracking_number')
                          && (
                            <TableData className={tableCellClassName}>
                              {orderEbaySendResult && (
                                orderEbaySendResult.Ack === 'Success' ? (
                                  <span className={trackingIconClassName} title="Tracking number sent to eBay">
                                    <IconOk color="#50CD89FF" width={14} />
                                  </span>
                                ) : null
                              )}
                              {trackingArr.map((trac, i) => (
                                <Fragment key={trac}>
                                  <a
                                    href={prepareProviderLink(trac)}
                                    className="globalLink"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {trac}
                                  </a>
                                  {i !== trackingArr.length - 1 && ', '}
                                </Fragment>
                              ))}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_assembly_supplier')
                          && (
                            <TableData className={tableCellClassName}>
                              {supplier}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_part_box_by')
                          && (
                            <TableData className={tableCellClassName}>
                              {orderPartBoxBy}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_picked_by')
                          && (
                            <TableData className={tableCellClassName}>
                              {orderPickedBy}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_assembled_by')
                          && (
                            <TableData className={tableCellClassName}>
                              {orderAssembledBy}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_packed_by')
                          && (
                            <TableData className={tableCellClassName}>
                              {orderPackedBy}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_picked_date')
                          && (
                            <TableData className={tableCellClassName}>
                              {pickedDate
                                && (
                                  <Badge
                                    type={ButtonTypes.LIGHT}
                                    text={DateTime.fromFormat(pickedDate, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' })
                                      .setLocale()
                                      .toFormat('yyyy-MM-dd HH:mm')}
                                  />
                                )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_assembled_date')
                          && (
                            <TableData className={tableCellClassName}>
                              {assembledDate
                                && (
                                  <Badge
                                    type={ButtonTypes.LIGHT}
                                    text={DateTime.fromFormat(assembledDate, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' })
                                      .setLocale('en')
                                      .toFormat('yyyy-MM-dd HH:mm')}
                                  />
                                )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_eta_date')
                          && (
                            <TableData className={tableCellClassName}>
                              {ETA && (
                                <Badge
                                  type={ButtonTypes.LIGHT}
                                  text={ETA?.status}
                                  info={ETA?.po ? (
                                    <>
                                      <Text text="PO:" />
                                      <a
                                        href={`/coilovers/ordered-parts/?query=${ETA?.po?.join(',')}`}
                                        className="globalLink"
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {ETA?.po?.join('|')}
                                      </a>
                                    </>
                                  ) : undefined}
                                />
                              )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_assembly_cost')
                          && (
                            <TableData className={tableCellClassName}>
                              {cost
                                ? `$${cost}`
                                : (
                                  <div
                                    data-for={`missing-order-cost-${_id}`}
                                    data-tip=""
                                  >
                                    <IconWarningTriangle
                                      color="#FFB885"
                                      width={30}
                                      className="d-flex"
                                      data-for={`missing-order-cost-${_id}`}
                                    />
                                    <ReactTooltip
                                      id={`missing-order-cost-${_id}`}
                                      place="top"
                                    >
                                      Missing order cost
                                    </ReactTooltip>
                                  </div>
                                )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('order_assembly_price')
                          && (
                            <TableData className={tableCellClassName}>
                              {price
                                ? `$${price}`
                                : (
                                  <div
                                    data-for={`missing-order-price-${_id}`}
                                    data-tip=""
                                  >
                                    <IconWarningTriangle
                                      color="#FFB885"
                                      width={30}
                                      className="d-flex"
                                      data-for={`missing-order-price-${_id}`}
                                    />
                                    <ReactTooltip
                                      id={`missing-order-price-${_id}`}
                                      place="top"
                                    >
                                      Missing order price
                                    </ReactTooltip>
                                  </div>
                                )}
                            </TableData>
                          )
                        }
                        {
                          coOrderTableSetting.includes('history')
                          && (
                            <TableData className={tableCellClassName}>
                              <a href={`/user-logs/?query=${_id}`} target="_blank" rel="noreferrer">
                                {_id || ''}
                              </a>
                            </TableData>
                          )
                        }
                      </TableRow>
                      {detailsExpanded && <CoOrderDetailsRow order={orderData} />}
                    </Fragment>
                  );
                })
                  : (
                    <EmptyTable colSpan={getColumn().length + 3} text="No any data find" />
                  )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {activeId && (
          <>
            <CoOrderCreateLabelModal
              visible={labelModalVisibility}
              toggleVisible={() => setLabelModalVisibility(false)}
              orderId={activeId}
              onCancel={() => {
                setLabelModalVisibility(false);
              }}
            />
            <ConfirmModal
              visible={confirmModal}
              toggleVisible={() => setConfirmModal(false)}
              confirmButtonText={actionData[actionType]?.buttonText}
              confirmText={actionData[actionType]?.confirmText}
              onConfirm={actionData[actionType]?.onConfirm}
              loading={actionLoading}
            />
            <CoMissingPartsModal
              visible={modalMissingParts}
              toggleVisible={() => {
                setModalMissingParts(false);
              }}
              orderId={activeId}
            />
            <CoOrderViewInvoiceModal
              visible={modalViewInvoice}
              orderId={activeId}
              toggleVisible={() => setModalViewInvoice(false)}
            />
          </>
        )}
        {lateCorrectionItem && (
          <PermissionsGate can={permissions.ADMIN_ADMIN}>
            <CoOrderLateCorrectionModal
              item={lateCorrectionItem}
              onClose={() => setLateCorrectionItem(null)}
            />
          </PermissionsGate>
        )}
        {editItem && (
          <CoOrderEditModal
            visible={editModalVisibility}
            toggleVisible={() => setEditModalVisibility(false)}
            item={editItem}
          />
        )}
        {actionResult && (
          <CoOrderAvailabilityModal
            visible={availabilityModalVisibility}
            toggleVisible={() => {
              setAvailabilityModalVisibility(false);
              dispatch(clearActionCoOrder());
            }}
            data={actionResult || {}}
          />
        )}
        <ConfirmModal
          confirmButtonText="Print one more"
          confirmText="Label sent to printer."
          visible={confirmPrintLabelModal}
          toggleVisible={() => setConfirmPrintLabelModal(false)}
          onCancel={() => {
            batch(() => {
              setConfirmPrintLabelModal(false);
              setActiveLabelFile(false);
              setActiveLabelFile2(false);
            });
          }}
          onConfirm={() => {
            onPrintLabel(activeLabelFile);
            onPrintLabel(activeLabelFile2);
          }}
        />
        <PersonalReportDrawer />
      </PermissionsGate>
    </Layout>
  );
};

export default Orders;
